import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { validateClassCode, registerStudent } from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle';
import './Join.css';

const Join = () => {
  const [step, setStep] = useState(1); // 1: Enter code, 2: Enter credentials
  const [classCode, setClassCode] = useState('');
  const [classInfo, setClassInfo] = useState(null);
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    name: '',
    confirmPassword: ''
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const { handleRequest, loading } = useApi();
  const navigate = useNavigate();

  const handleCodeChange = (e) => {
    setClassCode(e.target.value.toUpperCase());
    setErrorMessage('');
  };

  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!classCode.trim()) {
      setErrorMessage('Vennligst skriv inn en klassekode');
      return;
    }

    setValidatingCode(true);
    setErrorMessage('');
    try {
      const response = await handleRequest(validateClassCode, { classCode: classCode.trim() });
      setClassInfo(response.data);
      setStep(2);
    } catch (error) {
      setErrorMessage(error.message || 'Ugyldig klassekode. Sjekk koden og prøv igjen.');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passordene matcher ikke');
      return;
    }
    try {
      await handleRequest(registerStudent, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        classCode: classCode.trim()
      });
      navigate('/login', { state: { message: 'Registrering fullført! Vennligst logg inn.' } });
    } catch (error) {
      setErrorMessage(error.message || 'Registrering mislyktes. Prøv igjen.');
    }
  };

  const handleBack = () => {
    setStep(1);
    setClassInfo(null);
    setFormData({ email: '', password: '', name: '', confirmPassword: '' });
    setErrorMessage('');
  };

  return (
    <div className="join-container">
      <div className="dark-mode-toggle-wrapper">
        <DarkModeToggle />
      </div>
      <div className="join-card">
        <h2>Bli med i klasse</h2>
        {errorMessage && (
          <ErrorCard 
            message={errorMessage} 
            onClose={() => setErrorMessage('')}
          />
        )}

        {step === 1 ? (
          <div className="join-step">
            <p className="step-description">
              Skriv inn klassekoden for å komme i gang. Læreren din skal ha gitt deg denne koden.
            </p>
            <form onSubmit={handleValidateCode}>
              <div className="form-group">
                <label htmlFor="classCode">Klassekode</label>
                <input
                  type="text"
                  id="classCode"
                  name="classCode"
                  value={classCode}
                  onChange={handleCodeChange}
                  placeholder="Skriv inn klassekode"
                  required
                  autoFocus
                  className="code-input"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <button type="submit" disabled={validatingCode || !classCode.trim()}>
                {validatingCode ? 'Validerer...' : 'Fortsett'}
              </button>
            </form>
            <p className="auth-link">
              Er du lærer? <Link to="/register">Registrer deg her</Link>
            </p>
          </div>
        ) : (
          <div className="join-step">
            {classInfo && (
              <div className="class-info-box">
                <h3>Klassinformasjon</h3>
                <p><strong>Klasse:</strong> {classInfo.className}</p>
                <p><strong>Lærer:</strong> {classInfo.teacherName}</p>
              </div>
            )}
            <p className="step-description">
              Opprett en konto for å bli med i denne klassen.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Fullt navn</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Skriv inn fullt navn"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">E-post</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Skriv inn e-post"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Passord</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  placeholder="Lag et passord"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Bekreft passord</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleFormChange}
                  placeholder="Bekreft passordet ditt"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={handleBack} className="back-btn">
                  Tilbake
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Oppretter konto...' : 'Bli med i klasse'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Join;
